import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianDate } from '../utils/format';
import { useToast } from './ToastHost';

const Users = ({ user }) => {
  const location = useLocation();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [statusFilter, setStatusFilter] = useState('ACTIVE');
  const [searchQ, setSearchQ] = useState('');
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });
  const [bdmUsers, setBdmUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [salesUserRequests, setSalesUserRequests] = useState([]);
  const [salesReqBusy, setSalesReqBusy] = useState(false);
  const [bdmSalesMappings, setBdmSalesMappings] = useState([]);
  const [bdmSalesBusy, setBdmSalesBusy] = useState(false);
  const [directSales, setDirectSales] = useState([]);
  const [salesReqOpen, setSalesReqOpen] = useState(false);
  const [salesReq, setSalesReq] = useState({ name: '', email: '', password: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [page, setPage] = useState(1);
  const pageRef = useRef(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    role: 'SALES',
    status: 'ACTIVE',
    manager_id: '',
    client_id: ''
  });
  const [resetPassword, setResetPassword] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState({});
  const [bulkStatus, setBulkStatus] = useState('ACTIVE');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'SALES',
    client_id: ''
  });

  useEffect(() => {
    pageRef.current = Number(page) || 1;
  }, [page]);

  const fetchUsers = useCallback(async (nextPage) => {
    try {
      const resolvedPage = Number(nextPage ?? pageRef.current) || 1;
      const response = await api.get('/api/users', {
        params: {
          page: resolvedPage,
          limit,
          status: user.role === 'ADMIN' ? statusFilter : undefined,
          q: user.role === 'ADMIN' && searchQ ? searchQ : undefined,
          include_counts: user.role === 'ADMIN' ? true : undefined
        }
      });

      const payload = response.data;
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        setUsers(payload.items);
        setTotal(payload.total || 0);
        setPage(Number(payload.page) || resolvedPage);
        if (user.role === 'ADMIN' && payload.counts) {
          setStatusCounts({
            active: Number(payload.counts.active) || 0,
            inactive: Number(payload.counts.inactive) || 0
          });
        }
      } else {
        setUsers(Array.isArray(payload) ? payload : []);
        setTotal(Array.isArray(payload) ? payload.length : 0);
        setPage(resolvedPage);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [limit, searchQ, statusFilter, user.role]);

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/api/clients');
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  }, []);

  const fetchBdmUsers = useCallback(async () => {
    try {
      const res = await api.get('/api/users', { params: { all: true, role: 'BDM', status: 'ACTIVE' } });
      setBdmUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching BDM users:', error);
    }
  }, []);

  useEffect(() => {
    fetchUsers(1);
    fetchBdmUsers();
    fetchClients();
  }, [fetchBdmUsers, fetchClients, fetchUsers]);

  useEffect(() => {
    if (user.role !== 'ADMIN') return;
    const t = setTimeout(() => {
      fetchUsers(1);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchUsers, searchQ, statusFilter, user.role]);

  const fetchMySalesTeam = useCallback(async () => {
    if (user.role !== 'BDM') return;
    try {
      setBdmSalesBusy(true);
      const [mapRes, reqRes, directRes] = await Promise.all([
        api.get('/api/bdm-sales-mapping'),
        api.get('/api/sales-user-requests'),
        api.get('/api/users', { params: { all: true } })
      ]);

      setBdmSalesMappings(Array.isArray(mapRes.data) ? mapRes.data : []);
      setSalesUserRequests(Array.isArray(reqRes.data) ? reqRes.data : []);

      const arr = Array.isArray(directRes.data) ? directRes.data : [];
      setDirectSales(
        arr.filter(
          (u) => String(u.role) === 'SALES' && String(u.status) === 'ACTIVE' && String(u.manager_id || '') === String(user.id)
        )
      );
    } catch (e) {
      setBdmSalesMappings([]);
      setSalesUserRequests([]);
      setDirectSales([]);
    } finally {
      setBdmSalesBusy(false);
    }
  }, [user.id, user.role]);

  useEffect(() => {
    fetchMySalesTeam();
  }, [fetchMySalesTeam]);

  const fetchSalesUserRequests = useCallback(async () => {
    if (user.role !== 'ADMIN') return;
    try {
      setSalesReqBusy(true);
      const res = await api.get('/api/sales-user-requests', { params: { status: 'PENDING' } });
      setSalesUserRequests(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setSalesUserRequests([]);
    } finally {
      setSalesReqBusy(false);
    }
  }, [user.role]);

  useEffect(() => {
    fetchSalesUserRequests();
  }, [fetchSalesUserRequests]);

  useEffect(() => {
    if (!location.state?.openForm) return;

    setShowForm(true);

    const presetRole = location.state?.presetRole;
    const presetClientId = location.state?.presetClientId;
    const presetName = location.state?.presetName;
    const presetEmail = location.state?.presetEmail;

    setFormData((prev) => ({
      ...prev,
      role: presetRole || prev.role,
      client_id: presetRole === 'CLIENT' ? String(presetClientId || '') : prev.client_id,
      name: presetName || prev.name,
      email: presetEmail || prev.email
    }));
  }, [location.state]);

  const startEdit = (u) => {
    if (user.role !== 'ADMIN') return;
    setEditingId(u.id);
    setEditData({
      name: u.name || '',
      email: u.email || '',
      role: u.role || 'SALES',
      status: u.status || 'ACTIVE',
      manager_id: u.manager_id || '',
      client_id: u.client_id || ''
    });
    setResetPassword('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setResetPassword('');
  };

  const closeCreate = () => {
    setShowForm(false);
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const saveEdit = async (userId) => {
    try {
      const payload = {
        ...editData,
        manager_id: editData.role === 'SALES' ? (editData.manager_id || null) : null,
        client_id: editData.role === 'CLIENT' ? (editData.client_id || null) : null
      };
      await api.put(`/api/users/${userId}`, payload);

      if (resetPassword && resetPassword.length >= 6) {
        await api.put(`/api/users/${userId}/password`, { password: resetPassword });
      }

      setEditingId(null);
      setResetPassword('');
      fetchUsers(pageRef.current);
      fetchBdmUsers();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    try {
      await api.post('/api/users', {
        ...formData,
        client_id: formData.role === 'CLIENT' ? (formData.client_id || null) : null
      });
      setCreateSuccess('User created successfully');
      setShowForm(false);
      setFormData({
        name: '',
        email: '',
        password: '',
        role: 'SALES',
        client_id: ''
      });
      fetchUsers(1);
      fetchBdmUsers();
      fetchClients();
    } catch (error) {
      console.error('Error creating user:', error);
      setCreateError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const reviewSalesUserRequest = async (id, status) => {
    if (user.role !== 'ADMIN') return;
    try {
      setSalesReqBusy(true);
      await api.post(`/api/sales-user-requests/${id}/review`, { status });
      await fetchSalesUserRequests();
      await fetchUsers(1);
    } catch (e) {
      console.error('Error reviewing sales user request:', e);
    } finally {
      setSalesReqBusy(false);
    }
  };

  const submitSalesUserRequest = async () => {
    if (user.role !== 'BDM') return;
    if (!salesReq.name || !salesReq.email || !salesReq.password) {
      toast.error('Name, email and password are required');
      return;
    }
    setSalesReqBusy(true);
    try {
      await api.post('/api/sales-user-requests', {
        name: salesReq.name,
        email: salesReq.email,
        password: salesReq.password,
        notes: salesReq.notes
      });
      toast.success('Request sent to admin');
      setSalesReqOpen(false);
      setSalesReq({ name: '', email: '', password: '', notes: '' });
      await fetchMySalesTeam();
      await fetchUsers(1);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to submit request');
    } finally {
      setSalesReqBusy(false);
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading users...</div>;
  }

  const pageNum = Number(page) || 1;
  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = pageNum > 1;
  const canNext = pageNum < totalPages;
  const selectedCount = Object.values(selectedUserIds).filter(Boolean).length;

  const toggleSelectAllOnPage = (checked) => {
    const next = { ...selectedUserIds };
    for (const u of users) {
      next[u.id] = checked;
    }
    setSelectedUserIds(next);
  };

  const applyBulkStatus = async () => {
    const ids = Object.entries(selectedUserIds)
      .filter(([, v]) => v)
      .map(([k]) => Number(k))
      .filter(Boolean);
    if (ids.length === 0) return;
    try {
      setBulkBusy(true);
      await api.put('/api/users/bulk', { ids, status: bulkStatus });
      toast.success('Bulk update successful');
      setSelectedUserIds({});
      await fetchUsers(pageRef.current);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Bulk update failed');
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4 flex-wrap">
          <h1 className="text-2xl font-bold">Users</h1>

          {user.role === 'ADMIN' ? (
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                className="status-badge"
                onClick={() => {
                  setStatusFilter('ACTIVE');
                  setPage(1);
                }}
                style={{
                  cursor: 'pointer',
                  borderColor: statusFilter === 'ACTIVE' ? 'rgba(40, 167, 69, 0.55)' : 'rgba(255, 255, 255, 0.16)',
                  background: statusFilter === 'ACTIVE' ? 'rgba(40, 167, 69, 0.90)' : 'rgba(40, 167, 69, 0.14)',
                  color: statusFilter === 'ACTIVE' ? '#ffffff' : 'var(--text-secondary)',
                  boxShadow: statusFilter === 'ACTIVE' ? '0 10px 30px rgba(40, 167, 69, 0.28)' : undefined
                }}
              >
                Active {statusCounts.active}
              </button>
              <button
                type="button"
                className="status-badge"
                onClick={() => {
                  setStatusFilter('INACTIVE');
                  setPage(1);
                }}
                style={{
                  cursor: 'pointer',
                  borderColor: statusFilter === 'INACTIVE' ? 'rgba(220, 53, 69, 0.55)' : 'rgba(255, 255, 255, 0.16)',
                  background: statusFilter === 'INACTIVE' ? 'rgba(220, 53, 69, 0.90)' : 'rgba(220, 53, 69, 0.14)',
                  color: statusFilter === 'INACTIVE' ? '#ffffff' : 'var(--text-secondary)',
                  boxShadow: statusFilter === 'INACTIVE' ? '0 10px 30px rgba(220, 53, 69, 0.22)' : undefined
                }}
              >
                Inactive {statusCounts.inactive}
              </button>
              <input
                className="glass-input"
                placeholder="Search"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                style={{ width: 220 }}
              />
            </div>
          ) : null}
        </div>
        {user.role === 'ADMIN' ? (
          <button
            onClick={() => setShowForm(true)}
            className="glass-button primary"
          >
            + Add User
          </button>
        ) : null}

      </div>

      {user.role === 'ADMIN' && users.length > 0 ? (
        <div className="glass-card p-4 mb-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={users.length > 0 && users.every((u) => Boolean(selectedUserIds[u.id]))}
                onChange={(e) => toggleSelectAllOnPage(e.target.checked)}
              />
              Select page
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-tertiary">Selected: {selectedCount}</span>
              <select className="glass-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} disabled={bulkBusy}>
                <option value="ACTIVE">Set ACTIVE</option>
                <option value="INACTIVE">Set INACTIVE</option>
              </select>
              <button
                type="button"
                className="glass-button primary"
                onClick={applyBulkStatus}
                disabled={bulkBusy || selectedCount === 0}
              >
                {bulkBusy ? 'Please wait...' : 'Apply'}
              </button>
              <button
                type="button"
                className="glass-button"
                onClick={() => setSelectedUserIds({})}
                disabled={bulkBusy || selectedCount === 0}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {user.role === 'ADMIN' && (
        <div className="glass-card p-6 mb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Sales User Requests</h2>
              <div className="text-sm text-tertiary">BDM-created users pending admin approval</div>
            </div>
            <button type="button" className="glass-button" onClick={fetchSalesUserRequests} disabled={salesReqBusy}>
              {salesReqBusy ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {salesUserRequests.length === 0 ? (
            <div className="text-sm text-secondary mt-4">No pending requests</div>
          ) : (
            <div className="grid gap-3 mt-4">
              {salesUserRequests.map((r) => (
                <div key={r.id} className="p-4 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{r.name}</div>
                      <div className="text-sm text-secondary truncate">{r.email}</div>
                      <div className="text-xs text-tertiary mt-1">Requested by: {r.bdm_name} • {formatIndianDate(r.requested_at)}</div>
                      {r.notes ? <div className="text-xs text-tertiary mt-2">Notes: {r.notes}</div> : null}
                    </div>
                    <div className="flex gap-2">
                      <button type="button" className="glass-button" onClick={() => reviewSalesUserRequest(r.id, 'REJECTED')} disabled={salesReqBusy}>
                        Reject
                      </button>
                      <button type="button" className="glass-button primary" onClick={() => reviewSalesUserRequest(r.id, 'APPROVED')} disabled={salesReqBusy}>
                        Approve
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {user.role === 'BDM' && (
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">My Sales Team</h2>
              <div className="text-sm text-tertiary">Direct Team + Requests</div>
            </div>
            <div className="flex gap-2">
              <button type="button" className="glass-button primary" onClick={() => setSalesReqOpen(true)}>
                + Request Sales User
              </button>
              <button type="button" className="glass-button" onClick={fetchMySalesTeam} disabled={bdmSalesBusy}>
                {bdmSalesBusy ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {directSales.length > 0 && (
            <div className="mt-4">
              <div className="text-sm font-semibold mb-2">Direct Team</div>
              <div className="grid gap-2">
                {directSales.map((m) => (
                  <div key={`direct-${m.id}`} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="font-medium">{m.name}</div>
                    <div className="text-xs text-tertiary">{m.email}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-4">
            <div className="text-sm font-semibold mb-2">Pending User Requests</div>
            {salesUserRequests.filter((r) => r.status === 'PENDING').length === 0 ? (
              <div className="text-sm text-secondary">No pending requests</div>
            ) : (
              <div className="grid gap-2">
                {salesUserRequests.filter((r) => r.status === 'PENDING').map((r) => (
                  <div key={`req-p-${r.id}`} className="p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-tertiary">{r.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {salesReqOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(760px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Request New Sales User</h3>
              <button type="button" className="glass-button" onClick={() => setSalesReqOpen(false)} disabled={salesReqBusy}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input className="glass-input w-full" value={salesReq.name} onChange={(e) => setSalesReq((p) => ({ ...p, name: e.target.value }))} type="text" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input className="glass-input w-full" value={salesReq.email} onChange={(e) => setSalesReq((p) => ({ ...p, email: e.target.value }))} type="email" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Password</label>
                  <input className="glass-input w-full" value={salesReq.password} onChange={(e) => setSalesReq((p) => ({ ...p, password: e.target.value }))} type="text" placeholder="Min 6 characters" />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Notes (optional)</label>
                  <textarea className="glass-textarea w-full" rows="3" value={salesReq.notes} onChange={(e) => setSalesReq((p) => ({ ...p, notes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={() => setSalesReqOpen(false)} disabled={salesReqBusy}>
                  Cancel
                </button>
                <button type="button" className="glass-button primary" onClick={submitSalesUserRequest} disabled={salesReqBusy}>
                  {salesReqBusy ? 'Please wait...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showForm && user.role === 'ADMIN' ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Add New User</h3>
              <button type="button" className="glass-button" onClick={closeCreate}>
                ✕
              </button>
            </div>
            <div className="modal-body">

          {createError && (
            <div className="error-message mb-4">
              <span className="error-text">{createError}</span>
            </div>
          )}

          {createSuccess && (
            <div className="success-message mb-4">
              <span className="success-text">{createSuccess}</span>
            </div>
          )}

              <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="glass-input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="glass-input w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="glass-input w-full"
                required
                minLength="6"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
                className="glass-select w-full"
              >
                <option value="SALES">Sales</option>
                <option value="BDM">BDM</option>
                <option value="DEVELOPER">Developer</option>
                <option value="CLIENT">Client</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>

            {formData.role === 'CLIENT' && (
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-2">Client Company</label>
                <select
                  name="client_id"
                  value={formData.client_id}
                  onChange={handleChange}
                  className="glass-select w-full"
                  required
                >
                  <option value="">Select company</option>
                  {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
                    <option key={c.id} value={c.id}>{c.company || c.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="col-span-2 flex gap-2">
              <button type="submit" className="glass-button primary">
                Create User
              </button>
              <button
                type="button"
                onClick={closeCreate}
                className="glass-button"
              >
                Cancel
              </button>
            </div>
            </form>
            </div>
          </div>
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gap: 16,
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))'
        }}
      >
        {users.map((userItem) => (
          <div key={userItem.id} className="glass-card p-6">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                {user.role === 'ADMIN' ? (
                  <label className="text-xs text-tertiary" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <input
                      type="checkbox"
                      checked={Boolean(selectedUserIds[userItem.id])}
                      onChange={(e) => setSelectedUserIds((prev) => ({ ...prev, [userItem.id]: e.target.checked }))}
                    />
                    Select
                  </label>
                ) : null}
                <div className="text-lg font-semibold truncate">{userItem.name}</div>
                <div className="text-sm text-secondary truncate">{userItem.email}</div>
                <div className="text-xs text-tertiary mt-1">Created: {formatIndianDate(userItem.created_at)}</div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end flex-wrap">
                  {userItem.role ? <span className={`role-pill role-${String(userItem.role).toLowerCase()}`}>{userItem.role}</span> : null}
                  {userItem.status ? <span className={`status-badge status-${String(userItem.status).toLowerCase()}`}>{userItem.status}</span> : null}
                </div>
                <p className="text-xs text-tertiary mt-2">Joined: {formatIndianDate(userItem.created_at)}</p>
              </div>
            </div>

            {user.role === 'ADMIN' && editingId === userItem.id ? null : user.role === 'ADMIN' ? (
              <div className="flex gap-2 mt-4">
                <button type="button" className="glass-button" onClick={() => startEdit(userItem)}>
                  Edit
                </button>
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">
            Page {pageNum} of {totalPages} ({total} users)
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="glass-button"
              onClick={() => fetchUsers(pageNum - 1)}
              disabled={!canPrev}
            >
              Prev
            </button>
            <button
              type="button"
              className="glass-button"
              onClick={() => fetchUsers(pageNum + 1)}
              disabled={!canNext}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {users.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No users found</p>
        </div>
      )}

      {editingId && user.role === 'ADMIN' ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Edit User</h3>
              <button type="button" className="glass-button" onClick={cancelEdit}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input type="text" name="name" value={editData.name} onChange={handleEditChange} className="glass-input w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input type="email" name="email" value={editData.email} onChange={handleEditChange} className="glass-input w-full" required />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Role</label>
                  <select name="role" value={editData.role} onChange={handleEditChange} className="glass-select w-full">
                    <option value="SALES">Sales</option>
                    <option value="BDM">BDM</option>
                    <option value="DEVELOPER">Developer</option>
                    <option value="CLIENT">Client</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                </div>

                {editData.role === 'SALES' && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Manager (BDM)</label>
                    <select name="manager_id" value={editData.manager_id} onChange={handleEditChange} className="glass-select w-full">
                      <option value="">Unassigned</option>
                      {bdmUsers.map((bdm) => (
                        <option key={bdm.id} value={bdm.id}>{bdm.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {editData.role === 'CLIENT' && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Client Company</label>
                    <select name="client_id" value={editData.client_id} onChange={handleEditChange} className="glass-select w-full" required>
                      <option value="">Select company</option>
                      {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
                        <option key={c.id} value={c.id}>{c.company || c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select name="status" value={editData.status} onChange={handleEditChange} className="glass-select w-full">
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-2">Reset Password (optional)</label>
                  <input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} className="glass-input w-full" placeholder="Enter new password (min 6 chars)" minLength={0} />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={cancelEdit}>Cancel</button>
                <button type="button" className="glass-button primary" onClick={() => saveEdit(editingId)}>Save</button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Users;
